import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import BrandLogo from './BrandLogo';
import NotificationBell from './NotificationBell';
import { SearchTrigger } from './GlobalSearch';
import AvatarMenu from './ui/AvatarMenu';
import DirIcon from './ui/DirIcon';
import AttendanceControl from './attendance/AttendanceControl';
import { compactTitle, headerScrolledAway, usePageChromeStore } from '../hooks/usePageChrome';
import { useBackNavigation } from '../hooks/useBackNavigation';

/**
 * TopBar (T-30, PRD §4.3, controls inventory §5): calm by default.
 *
 * Desktop: search field (opens the command palette) · compact page title and
 * the page's one primary action (PageChrome: only once the page header has
 * scrolled under the bar) · attendance · bell · avatar menu. "+ חדש" moved to
 * the floating QuickCreate button (owner, 2026-10-06; supersedes Q-020b).
 * Mobile: the mark · attendance (compact) · search · bell (the avatar items live
 * in the "עוד" sheet); the compact title and action stay desktop-only so the
 * controls keep their room.
 * Attendance (owner, 2026-10-11): `attendance/AttendanceControl`, the one place
 * to clock in, take a break and clock out, on every screen.
 * "חזרה" (owner, 2026-10-07; `useBackNavigation`): at the start, on detail pages
 * only (round 2, 2026-10-11). Desktop: arrow + "חזרה" in a fixed slot, so the
 * search field keeps its place on every page; mobile: the arrow alone (44 px) in
 * place of the mark.
 *
 * Material: on the canvas at rest; `mat-chrome` glass with a hairline only
 * while content scrolls beneath (`html[data-scrolled]`, PRD §3.8).
 */
const SCROLL_EDGE = 8;

/** Scroll state: `data-scrolled` on <html>, and whether the page header has passed under the bar. */
function useScrollChrome(pathname: string, anchor: HTMLElement | null) {
  const [state, setState] = useState<{ away: boolean; h1Text: string | null }>({ away: false, h1Text: null });
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const y = window.scrollY;
      document.documentElement.toggleAttribute('data-scrolled', y > SCROLL_EDGE);
      const h1 = document.querySelector<HTMLElement>('#main h1');
      // A registered page header wins; otherwise the page h1 (any page gets the compact title).
      const target = anchor && anchor.isConnected ? anchor : h1;
      const chrome = document.querySelector('.topbar')?.getBoundingClientRect().bottom ?? 56;
      const away = headerScrolledAway(target?.getBoundingClientRect().bottom, chrome);
      const h1Text = away ? (h1?.textContent || '').trim() || null : null;
      setState((prev) => (prev.away === away && prev.h1Text === h1Text ? prev : { away, h1Text }));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [pathname, anchor]);
  useEffect(() => () => document.documentElement.removeAttribute('data-scrolled'), []);
  return state;
}

export default function TopBar({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const entry = usePageChromeStore((s) => s.entry);
  const { away, h1Text } = useScrollChrome(location.pathname, entry?.anchor ?? null);
  const title = away ? compactTitle(entry?.title, h1Text) : null;
  const action = entry?.action ?? null;
  const showAction = away && !!action;
  // Mounted while the page has an action (so it can fade in and out); hidden from
  // pointer, keyboard and screen readers until the header scrolls away.
  const shown = action;
  const back = useBackNavigation();
  const backLabel = t('shell.back.aria');

  return (
    <header className="topbar sticky top-0 z-chrome">
      <div className="flex h-14 items-center gap-2 px-4 sm:px-6 md:gap-3">
        {/* Desktop: a fixed slot, so the search field never moves when "חזרה" comes and goes. */}
        <div className="flex flex-shrink-0 items-center md:w-20">
          {back.visible ? (
            <button
              type="button"
              onClick={back.goBack}
              aria-label={backLabel}
              data-tip={backLabel}
              className="-ms-3 inline-flex h-11 w-11 flex-shrink-0 items-center justify-center gap-1.5 rounded-lg text-text-2 transition-colors duration-micro hover:bg-surface-subtle hover:text-text md:-ms-2.5 md:h-control md:w-auto md:px-2.5 md:text-sm md:font-medium"
              data-topbar-back=""
            >
              <DirIcon name="back" size={18} strokeWidth={1.75} />
              <span className="hidden md:inline">{t('shell.back.label')}</span>
            </button>
          ) : (
            <Link to="/" className="flex flex-shrink-0 items-center rounded-md md:hidden" aria-label={t('shell.sidebar.home')}>
              <BrandLogo variant="mark" decorative />
            </Link>
          )}
        </div>

        <div className="hidden min-w-0 md:block">
          <SearchTrigger />
        </div>

        <div className="flex min-w-0 flex-1 justify-center" aria-hidden={!title}>
          <p className="topbar-title hidden truncate text-sm font-semibold text-text md:block" data-visible={title ? '' : undefined}>
            {title}
          </p>
        </div>

        <div className="flex flex-shrink-0 items-center gap-1 md:gap-2">
          {shown && (
            <div
              className={`topbar-title hidden md:block ${showAction ? '' : 'invisible pointer-events-none'}`}
              data-visible={showAction ? '' : undefined}
              aria-hidden={showAction ? undefined : true}
            >
              <button
                type="button"
                onClick={shown.onClick}
                disabled={!showAction || shown.disabled}
                aria-busy={shown.busy || undefined}
                className="btn btn-primary btn-sm"
                data-page-chrome-action=""
                tabIndex={showAction ? undefined : -1}
              >
                {shown.busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : shown.icon}
                {shown.label}
                {shown.extra}
              </button>
            </div>
          )}
          <AttendanceControl />
          {/* C-30 (clarity, N-04): "התקנת התוסף" left the bar; it is a one-time notice under
              it (`ExtensionInstall` in Layout) and an item in the user menu. */}
          <div className="md:hidden">
            <SearchTrigger compact />
          </div>
          <NotificationBell />
          <div className="hidden md:block">
            <AvatarMenu onLogout={onLogout} />
          </div>
        </div>
      </div>
    </header>
  );
}
